import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiService, Appointment, AuditLog } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import IntakeAdminPanel from './IntakeAdminPanel';
import CompletionDialog from './billing/CompletionDialog';
import { formatDate as fmtLocaleDate, formatDateTime as fmtLocaleDateTime } from '../../i18n/locale';

const PUBLIC_SITE_BASE = process.env.NODE_ENV === 'production'
  ? 'https://seadent.es'
  : 'http://localhost:3001';

interface AppointmentModalProps {
  appointment: Appointment;
  onClose: () => void;
  onUpdate: () => void;
  onEdit: () => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({ appointment, onClose, onUpdate, onEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'details' | 'intake' | 'history'>('details');
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [intakeLinkCopied, setIntakeLinkCopied] = useState(false);

  const navigate = useNavigate();
  // "Behandlung abschließen" is offered for appointments that have actually
  // taken place (or are taking place now). Today/past + status that isn't
  // already terminal.
  const todayStr = new Date().toISOString().slice(0, 10);
  const isPastOrToday = (appointment.appointment_date || '') <= todayStr;
  const canComplete = isPastOrToday && ['confirmed', 'pending', 'scheduled'].includes(appointment.status);
  // Offers (Kostenvoranschlag) are useful before the appointment.
  const isFuture = !isPastOrToday;
  const canOfferQuote = isFuture && appointment.status !== 'cancelled' && !!appointment.patient_id;
  // Retro-Rechnung: Termin schon abgeschlossen, aber noch nicht abgerechnet.
  // Bewusst auch ohne patient_id erlaubt — der Editor fängt das ab und bietet
  // dort den Patient-Picker (+ Inline-Anleger) an.
  const canBillRetro = appointment.status === 'completed';

  const startOffer = () => {
    if (!appointment.patient_id) return;
    onClose();
    navigate(
      `/admin/invoices/new?type=offer&patient=${appointment.patient_id}&appointment=${appointment.id}&language=${appointment.language || 'de'}`
    );
  };

  const startRetroInvoice = () => {
    onClose();
    const lang = appointment.language || 'de';
    if (appointment.patient_id) {
      navigate(
        `/admin/invoices/new?type=invoice&patient=${appointment.patient_id}&appointment=${appointment.id}&language=${lang}`
      );
    } else {
      // Kein Patient verknüpft (z. B. Online-Buchung ohne Match) — Editor öffnet
      // im Picker-Stub. Termin-Verknüpfung übernimmt der Editor beim Create.
      navigate(`/admin/invoices/new?type=invoice&appointment=${appointment.id}&language=${lang}`);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchAuditLogs();
    }
  }, [activeTab, appointment.id]);

  const fetchAuditLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await apiService.getAppointmentAuditLogs(appointment.id);
      if (response.data.success) {
        setAuditLogs(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      addToast(t('appointments.modal.loadHistoryError'), 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const renderAuditAction = (log: AuditLog) => {
    const inv = (log.new_data || {}) as any;
    const num = inv.number || `#${inv.invoice_id ?? ''}`;
    switch (log.action) {
      case 'create': return t('appointments.modal.actionCreate');
      case 'update': return t('appointments.modal.actionUpdate');
      case 'cancel': return t('appointments.modal.actionCancel');
      case 'delete': return t('appointments.modal.actionDelete');
      case 'email_sent': return t('appointments.modal.actionEmailSent', { type: log.old_data?.type || log.new_data?.type || '' });
      case 'email_failed': return t('appointments.modal.actionEmailFailed');
      case 'invoice_finalized': return t('appointments.modal.actionInvoiceFinalized', { num });
      case 'offer_finalized': return t('appointments.modal.actionOfferFinalized', { num });
      case 'cancellation_finalized': return t('appointments.modal.actionCancellationFinalized', { num });
      case 'invoice_paid': return t('appointments.modal.actionInvoicePaid', { num });
      case 'invoice_canceled': return t('appointments.modal.actionInvoiceCanceled', { num });
      case 'cancellation_issued': return t('appointments.modal.actionCancellationIssued', { num });
      case 'invoice_emailed': return t('appointments.modal.actionInvoiceEmailed', { num, to: inv.to || '' });
      case 'invoice_email_failed': return t('appointments.modal.actionInvoiceEmailFailed', { num });
      default: return log.action;
    }
  };

  const renderAuditDetails = (log: AuditLog) => {
    if ((log.action === 'update' || log.action === 'cancel') && log.old_data && log.new_data) {
      // Felder, die im Diff nicht angezeigt werden: technische Zeitstempel und
      // Reste der abgeschalteten Patient-Match-Funktion (kommen aus Altdaten).
      const skipKeys = new Set(['updated_at', 'patient_match_candidates', 'patient_review_needed']);
      const sameValue = (a: unknown, b: unknown) =>
        a === b ||
        (a == null && b == null) ||
        (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
      const changes: React.ReactNode[] = [];
      for (const key in log.new_data) {
        if (skipKeys.has(key)) continue;
        if (sameValue(log.new_data[key], log.old_data[key])) continue;
        changes.push(
          <li key={key} className="text-gray-600">
            <span className="font-medium text-gray-700">{key}:</span> {String(log.old_data[key] || '-')} <span className="mx-1 text-gray-400">➔</span> {String(log.new_data[key] || '-')}
          </li>
        );
      }
      return changes.length > 0 ? <ul className="text-sm mt-2 pl-4 border-l-2 border-gray-200 space-y-1">{changes}</ul> : null;
    }
    if (log.action === 'email_failed' && log.old_data?.error) {
      return <p className="text-sm text-red-600 mt-1">{log.old_data.error}</p>;
    }
    return null;
  };

  const handleStatusChange = async (newStatus: Appointment['status']) => {
    try {
      setLoading(true);
      setError(null);
      
      if (newStatus === 'cancelled') {
        await apiService.cancelAppointment(appointment.id);
        addToast(t('appointments.modal.cancelledToast'), 'success');
      } else {
        await apiService.updateAppointment(appointment.id, { status: newStatus });
        if (newStatus === 'confirmed') {
          addToast(t('appointments.modal.confirmedToast'), 'success');
        } else {
          addToast(t('appointments.modal.statusUpdatedToast', { status: getStatusLabel(newStatus) }), 'success');
        }
      }

      onUpdate();
    } catch (err) {
      console.error('Error updating appointment:', err);
      setError(t('appointments.modal.updateError'));
      addToast(t('appointments.modal.updateError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('appointments.modal.confirmDelete'))) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteAppointment(appointment.id);
      onUpdate();
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError(t('appointments.modal.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  const openGoogleCalendar = () => {
    if (appointment.google_calendar_event_id) {
      const eventUrl = `https://calendar.google.com/calendar/event?eid=${appointment.google_calendar_event_id}`;
      window.open(eventUrl, '_blank');
    }
  };

  const intakeUrl = appointment.intake_token
    ? `${PUBLIC_SITE_BASE}/intake/${appointment.intake_token}`
    : '';
  const copyIntakeLink = async () => {
    if (!intakeUrl) return;
    try {
      await navigator.clipboard.writeText(intakeUrl);
      setIntakeLinkCopied(true);
      addToast(t('appointments.modal.intakeLinkCopied'), 'success');
      setTimeout(() => setIntakeLinkCopied(false), 2500);
    } catch {
      addToast(t('appointments.modal.intakeLinkCopyError'), 'error');
    }
  };
  const openIntakeLink = () => {
    if (!intakeUrl) return;
    window.open(intakeUrl, '_blank', 'noopener');
  };

  const formatPhoneNumber = (phone: string | null | undefined) => {
    if (!phone) return '—';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    return fmtLocaleDate(dateString, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return fmtLocaleDateTime(`2000-01-01T${timeString}`, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-purple-100 text-purple-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return t('appointments.modal.statusPending');
      case 'confirmed':
        return t('appointments.modal.statusConfirmed');
      case 'scheduled':
        return t('appointments.modal.statusScheduled');
      case 'completed':
        return t('appointments.modal.statusCompleted');
      case 'cancelled':
        return t('appointments.modal.statusCancelled');
      case 'no_show':
        return t('appointments.modal.statusNoShow');
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-gray-900">{t('appointments.modal.title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none ${activeTab === 'details' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('details')}
            >
              {t('appointments.modal.tabDetails')}
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none flex items-center gap-2 ${activeTab === 'intake' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('intake')}
            >
              {t('appointments.modal.tabIntake')}
              {appointment.intake_status === 'submitted' && (
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-label={t('appointments.modal.intakeSubmittedLabel')}></span>
              )}
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none ${activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('history')}
            >
              {t('appointments.modal.tabHistory')}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {activeTab === 'intake' && <IntakeAdminPanel appointment={appointment} />}

          {activeTab === 'details' && (
            <>

              {/* Erfassungsbogen-Banner: erscheint, sobald der Termin bestätigt ist.
                  Anamnese-Erhebung ist berufsrechtliche Pflicht; digitale Vorab-
                  Erfassung spart Wartezeit am Behandlungstag. */}
              {appointment.status === 'confirmed' && intakeUrl && (
                <div className={`mb-4 p-4 rounded-lg border-2 ${appointment.intake_status === 'submitted' ? 'bg-emerald-50 border-emerald-300' : 'bg-green-50 border-green-300'}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📋</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-gray-900">{t('appointments.modal.intakeBannerTitle')}</h3>
                        {appointment.intake_status === 'submitted' ? (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-200 text-emerald-900">
                            {t('appointments.modal.intakeSubmittedBadge')}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                            {t('appointments.modal.intakeWaitingBadge')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mb-3">
                        {t('appointments.modal.intakeBannerText')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={copyIntakeLink}
                          className="px-3 py-1.5 text-sm font-semibold bg-[#384860] text-white rounded hover:bg-[#263245]"
                        >
                          {intakeLinkCopied ? t('appointments.modal.copied') : t('appointments.modal.copyLink')}
                        </button>
                        <button
                          onClick={openIntakeLink}
                          className="px-3 py-1.5 text-sm font-semibold border border-[#384860] text-[#384860] bg-white rounded hover:bg-[#384860]/5"
                        >
                          {t('appointments.modal.openForm')}
                        </button>
                        <button
                          onClick={() => setActiveTab('intake')}
                          className="px-3 py-1.5 text-sm font-semibold border border-[#384860]/30 text-[#384860] bg-white rounded hover:bg-[#384860]/5"
                        >
                          {t('appointments.modal.sendMailToPatient')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Website-booking banner */}
          {appointment.source === 'website' && (
            <div className="mb-4 p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🌐</span>
                <div className="flex-1">
                  <h3 className="font-bold text-purple-900">{t('appointments.modal.websiteBannerTitle')}</h3>
                  <p className="text-sm text-purple-700">
                    {appointment.status === 'pending'
                      ? t('appointments.modal.websiteBannerPending')
                      : t('appointments.modal.websiteBannerOther')}
                  </p>
                  {appointment.insurance && (
                    <p className="text-sm mt-1 text-purple-700"><span className="font-medium">{t('appointments.modal.insuranceLabel')}</span> {appointment.insurance}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Patient Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('appointments.modal.patientInfo')}</h3>
              <div className="space-y-2">
                <p><span className="font-medium">{t('appointments.modal.name')}</span> {appointment.patient_name}</p>
                <p><span className="font-medium">{t('appointments.modal.email')}</span> {appointment.patient_email}</p>
                <p><span className="font-medium">{t('appointments.modal.phone')}</span> {formatPhoneNumber(appointment.patient_phone)}</p>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{t('appointments.modal.appointmentDetails')}</h3>
              <div className="space-y-2">
                <p><span className="font-medium">{t('appointments.modal.date')}</span> {formatDate(appointment.appointment_date)}</p>
                <p><span className="font-medium">{t('appointments.modal.time')}</span> {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}</p>
                <p><span className="font-medium">{t('appointments.modal.provider')}</span> {appointment.provider_name}</p>
                <p><span className="font-medium">{t('appointments.modal.procedure')}</span> {appointment.procedure_name}</p>
                <p><span className="font-medium">{t('appointments.modal.operatory')}</span> {appointment.operatory_name}</p>
                <p><span className="font-medium">{t('appointments.modal.status')}</span>
                  <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                    {getStatusLabel(appointment.status)}
                  </span>
                </p>
              </div>
            </div>

            {/* Notes */}
            {appointment.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">{t('appointments.modal.notes')}</h3>
                <p className="text-gray-700">{appointment.notes}</p>
              </div>
            )}

            {/* Google Calendar Link */}
            {appointment.google_calendar_event_id && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">{t('appointments.modal.googleCalendar')}</h3>
                <button
                  onClick={openGoogleCalendar}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {t('appointments.modal.viewInGoogleCalendar')}
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {t('appointments.modal.edit')}
            </button>

            {canComplete && (
              <button
                onClick={() => setShowCompletion(true)}
                className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary/40 font-semibold"
              >
                {t('appointments.modal.completeAndInvoice')}
              </button>
            )}

            {canOfferQuote && (
              <button
                onClick={startOffer}
                className="px-4 py-2 border border-brand-primary text-brand-primary rounded-md hover:bg-brand-accent/10 focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
              >
                {t('appointments.modal.createOffer')}
              </button>
            )}

            {canBillRetro && (
              <button
                onClick={startRetroInvoice}
                className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary/40 font-semibold"
              >
                {t('appointments.modal.createInvoice')}
              </button>
            )}

            {appointment.status === 'pending' && (
              <button
                onClick={() => handleStatusChange('scheduled')}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {t('appointments.modal.acceptScheduled')}
              </button>
            )}

            {(appointment.status === 'scheduled' || appointment.status === 'pending') && (
              <button
                onClick={() => handleStatusChange('confirmed')}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {t('appointments.modal.confirm')}
              </button>
            )}
            
            {/* Reiner "Complete"-Statuswechsel nur als Fallback, wenn der
                Rechnungs-Workflow (canComplete) nicht greift — z. B. bei
                zukünftigen Terminen, die vorzeitig auf completed gesetzt
                werden sollen. Im Normalfall führt "Abschließen & Rechnung"
                durch den Dialog, in dem "Ohne Rechnung abschließen" eine
                der vier Exit-Optionen ist. */}
            {appointment.status === 'confirmed' && !canComplete && (
              <button
                onClick={() => handleStatusChange('completed')}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {t('appointments.modal.complete')}
              </button>
            )}

            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
              <button
                onClick={() => handleStatusChange('cancelled')}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {t('appointments.modal.cancel')}
              </button>
            )}

            {appointment.status === 'confirmed' && (
              <button
                onClick={() => handleStatusChange('no_show')}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {t('appointments.modal.noShow')}
              </button>
            )}

              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {t('appointments.modal.delete')}
              </button>
            </div>
            </>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {logsLoading ? (
                <div className="text-center py-8 text-gray-500">{t('appointments.modal.loadingHistory')}</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">{t('appointments.modal.noHistory')}</div>
              ) : (
                <div className="relative border-l border-gray-200 ml-3 space-y-6">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="mb-8 ml-6">
                      <span className="absolute flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full -left-3 ring-8 ring-white">
                        <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </span>
                      <h3 className="flex items-center mb-1 text-md font-semibold text-gray-900">
                        {renderAuditAction(log)}
                      </h3>
                      <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                        {(() => {
                          const ts = log.changed_at || (log as any).created_at;
                          const d = ts ? new Date(ts) : null;
                          return d && !isNaN(d.getTime()) ? fmtLocaleDateTime(d, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
                        })()} • {t('appointments.modal.historyBy', { user: log.changed_by })}
                      </time>
                      {renderAuditDetails(log)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCompletion && (
        <CompletionDialog
          appointment={appointment}
          onClose={() => setShowCompletion(false)}
          onCompleted={() => { onUpdate(); }}
        />
      )}
    </div>
  );
};

export default AppointmentModal;